import { Analytics, EventProps } from "@faro-lotv/foreign-observers";
import { RouteParams } from "@router/route-params";
import { useAppParams } from "@router/router-helper";
import { selectedProjectSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector, loggedInUserSelector } from "@store/user/user-selector";
import { BreakpointExtended } from "@styles/common-styles";
import { getBreakpointName } from "@utils/ui-utils";
import { useLocation } from "react-router-dom";

/** Params for tracking an event */
export interface LogEventParams {
  /** Name of the event to track */
  name: string;

  /** Additional properties for the event */
  props?: EventProps;
}

/** All the ids available in the app */
const allEntityIds: Array<keyof RouteParams> = [
  "companyId",
  "projectId",
  "memberId",
  "groupId",
];

export interface UseTrackEvent {
  /**
   * Function that tracks an event in the background, returning immediately.
   * In most cases, this should be preferred over `trackAsyncEvent`.
   */
  trackEvent({ name, props }: LogEventParams): void;

  /**
   * Function that tracks an event, returning a Promise that waits for the result.
   * Waiting for the result is currently broken, but fixing it could lead to unexpected behavior.
   */
  trackAsyncEvent({ name, props }: LogEventParams): Promise<void>;
}

/** Default properties for the event */
interface DefaultProps {
  /** Role of the user in the workspace */
  workspaceRole?: string;

  /** Role of the user in the project */
  projectRole?: string;

  /**
   * For events we only care which breakpoint the user had when the event was triggered
   * For the exact screen size and orientation we track only on page load
   */
  windowWidthBreakpoint: BreakpointExtended;

  /** Path of the app when the event was triggered */
  path: string;

  /** ID of the user triggering the event */
  userId?: string;
}

/** Track events with additional default properties that might be changed over the course */
export function useTrackEvent(): UseTrackEvent {
  const { pathname } = useLocation();
  const params = useAppParams();
  const currentUser = useAppSelector(currentUserSelector);
  const selectedProject = useAppSelector(selectedProjectSelector);
  const loggedInUser = useAppSelector(loggedInUserSelector);

  /**
   * Gets the props for the event
   */
  function getProps(props?: EventProps): EventProps {
    const defaultProps: DefaultProps = {
      windowWidthBreakpoint: getBreakpointName(window.innerWidth),
      workspaceRole: currentUser?.role?.toString(),
      projectRole: selectedProject?.role?.toString(),
      path: createPathForTracking(pathname, params),
      userId: loggedInUser?.id,
    };

    return {
      ...defaultProps,
      ...props,
    };
  }

  function trackEvent({ name, props }: LogEventParams): void {
    Analytics.track(name, getProps(props));
  }

  /**
   * TODO: The LOTV package does not offer an async tracking method right now, only fire and forget.
   */
  async function trackAsyncEvent({
    name,
    props,
  }: LogEventParams): Promise<void> {
    trackEvent({ name, props });
    return Promise.resolve();
  }

  return { trackEvent, trackAsyncEvent };
}

/**
 * Creates the path parameter for tracking by replacing the actual entityIds with its name
 * For example the ID of company changes to "companyId" in the pathname
 * encodeURIComponent is used to replaces the encoded dynamicId with staticId in the pathname.
 * If memberId is foo@bar in the pathname it becomes foo%40bar and need to be encoded to be replaced with memberId
 */
export function createPathForTracking(
  originalPathname: string,
  params: Readonly<Partial<RouteParams>>
): string {
  try {
    let replacedPath = originalPathname;
    for (const [paramName, paramValue] of Object.entries(params)) {
      if (allEntityIds.includes(paramName as keyof RouteParams)) {
        // Replaces the encoded dynamicId with staticId in the pathname.
        // This is used as some characters are encoded in the pathname, e.g. foo@bar -> foo%40bar
        replacedPath = replacedPath.replace(
          encodeURIComponent(paramValue),
          paramName
        );
      }
    }
    return replacedPath;
  } catch (error) {
    // If there is an error in the function, log the error and return an empty string
    // Therefore the app doesn't crash and no sensitive data like project id is sent to the analytic service
    // eslint-disable-next-line no-console
    console.error("Error in createPathForTracking", error);
    return "";
  }
}
